/* eslint-disable max-classes-per-file */
import { } from './../base/annotation';
import { Metamodel } from '../base/model';
import { Named } from '../base/named';

// These represent valid object transitions
// New -> Fetched (after save)
//     -> Dirty (after edits)
//     -> Broken (after references are broken)
//     -> Deleted (deleted - no op)
//     -> Zombie (no op)
// Fetched -> Dirty
//         -> Deleted
//         -> Zombie
//         -> Listed
//         -> Broken

// Proxy -> (replaced)

//  Listed -> Fetched
//         -> Deleted
// States in combinations (New | (Dirty | Deleted | Broken))
//                        (Fetched | (Dirty | Deleted | Broken))
//                        (Zombie)
export enum eModelState {
    NEW = 1, // Object when first created. Can attach a lifeline object
    LISTED = 2, // Listed which has just name and description
    FETCHED = 4, // Object when fetched from repository
    DIRTY = 8, // Object when edits are done
    DELETED = 16, // Object when deleted
    ZOMBIE = 32, // Deleted object when saved goes into zombie state
    BROKEN = 64,  // Set when a reference used in this object gets deleted
    PROXY = 128,   // A proxy is a reference that will filled up. A proxy will never transition to anything
    SAVED = 256 // Object has been saved in user store
}

export class ModelState {
    public state: number = 0;      // Object will be in one of the state. No model state is assumed new
    public lifeline?: Named;
    public lastSavedAt?: Date; // Last saved at
    public lastSaveBy?: string; // User, subscription?
}

export class ModelMarker {
    private constructor() { }

    public static markDirty(named: Named, erase: boolean = false): Named {
        return ModelMarker.mark(named, eModelState.DIRTY, erase);
    }

    /*isMark
        A life line object determines if this object exists or needs to be deleted
        So if the object is assumed deleted if the lifeline is deleted.
        You can explicitly detach a lifeline from a object(say the new object was used in a second object)
    */
    public static markNew(named: Named, lifeline?: Named): Named {
        ModelMarker.mark(named, eModelState.NEW, true);

        if (lifeline) {
            ModelMarker.offerLifeline(named, lifeline);
        }
        return named;
    }

    public static markDelete(named: Named): Named {
        ModelMarker.removeLifeline(named);
        return ModelMarker.mark(named, eModelState.DELETED, false);
    }

    public static markBroken(named: Named): Named {
        return ModelMarker.mark(named, eModelState.BROKEN, false);
    }

    public static markZombie(named: Named): Named {
        ModelMarker.removeLifeline(named);
        return ModelMarker.mark(named, eModelState.ZOMBIE, true);
    }

    public static markFetched(named: Named): Named {
        ModelMarker.removeLifeline(named);
        return ModelMarker.mark(named, eModelState.FETCHED, true);
    }

    public static markListed(named: Named): Named {
        ModelMarker.removeLifeline(named);
        return ModelMarker.mark(named, eModelState.LISTED, true);
    }

    public static markProxy(named: Named): Named {
        return ModelMarker.mark(named, eModelState.PROXY, true);
    }

    public static markSaved(named: Named, erase: boolean = false): Named {
        return ModelMarker.mark(named, eModelState.SAVED, erase);
    }

    public static markUnsaved(named: Named): Named {
        if (!named.extension._state$) {
            named.extension._state$ = new ModelState();
        }
        // eslint-disable-next-line no-bitwise
        const unsaved = (~eModelState.SAVED);
        // eslint-disable-next-line no-bitwise
        (named.extension._state$ as ModelState).state &= unsaved;

        return named;
    }

    public static isDirty(named: Named): boolean {
        return ModelMarker.isMarked(named, eModelState.DIRTY);
    }

    public static isBroken(named: Named): boolean {
        return ModelMarker.isMarked(named, eModelState.BROKEN);
    }

    public static isNew(named: Named): boolean {
        return ModelMarker.isMarked(named, eModelState.NEW);
    }

    public static isListed(named: Named): boolean {
        return ModelMarker.isMarked(named, eModelState.LISTED);
    }

    public static isZombie(named: Named): boolean {
        return ModelMarker.isMarked(named, eModelState.ZOMBIE);
    }

    public static isFetched(named: Named): boolean {
        return ModelMarker.isMarked(named, eModelState.FETCHED);
    }

    public static isProxy(named: Named): boolean {
        return ModelMarker.isMarked(named, eModelState.PROXY);
    }

    public static isSaved(named: Named): boolean {
        return ModelMarker.isMarked(named, eModelState.SAVED);
    }

    public static isDeleted(named: Named): boolean {
        const lifeline = ModelMarker.lifeline(named);
        if (lifeline && (ModelMarker.isDeleted(lifeline) || ModelMarker.isListed(lifeline) || ModelMarker.isZombie(lifeline))) {
            return true;
        }
        const deleted = ModelMarker.isMarked(named, eModelState.DELETED);

        if (deleted) {
            return true;
        }

        const lifeCycles = Metamodel.modelOf(named).tag<string[]>(Named.LIFECYCLE);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (lifeCycles) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const namedAny = (named as any);
            for (const lifeCycle of lifeCycles) { // If any of them are deleted, then this is deleted too
                if (namedAny[lifeCycle] && ModelMarker.isDeleted(namedAny[lifeCycle])) {
                    return true;
                }
            }
        }

        return false;
    }

    public static isUnmarked(named: Named): boolean {
        return named.extension._state$ === undefined;
    }

    /**
     * Return true if the named hasn't been published/committed
     */
    public static isNewInPublish(named: Named): boolean {
        // New -> neither publish nor autosave;
        // dirty but not fetch -> not publish but autosave
        return ModelMarker.isNew(named) || (ModelMarker.isDirty(named) && !ModelMarker.isFetched(named));
    }

    public static lifeline(named: Named): Named | undefined {
        return named.extension._state$ ?
            // eslint-disable-next-line no-bitwise
            (named.extension._state$ as ModelState).lifeline : undefined;
    }

    public static getMarkingState(named: Named): {} | undefined {
        return named.extension._state$ || undefined;
    }

    public static restoreMarkingState(named: Named, markingState: {} | undefined): void {
        named.extension._state$ = markingState;
    }

    private static mark(named: Named, state: eModelState, erase: boolean): Named {
        if (!named.extension._state$) {
            named.extension._state$ = new ModelState();
        }

        if (!erase) {
            // eslint-disable-next-line no-bitwise
            (named.extension._state$ as ModelState).state |= state;
        } else {
            (named.extension._state$ as ModelState).state = state;
        }
        return named;
    }

    private static isMarked(named: Named, state: eModelState): boolean {
        return named?.extension?._state$ ?
            // eslint-disable-next-line no-bitwise
            (((named.extension._state$ as ModelState).state & state) === state) : false;
    }

    private static offerLifeline(named: Named, lifeline: Named): void {
        if (!named.extension._state$) {
            named.extension._state$ = new ModelState();
        }
        (named.extension._state$ as ModelState).lifeline = lifeline;
    }

    private static removeLifeline(named: Named): void {
        if (named.extension._state$ && (named.extension._state$ as ModelState).lifeline) {
            delete (named.extension._state$ as ModelState).lifeline;
        }
    }
}
